import type { Theme } from "@mui/material";
import { Collapse, Grid, Paper, Stack } from "@mui/material";
import type { DragEvent, FC, PropsWithChildren, ReactNode } from "react";
import { useState } from "react";
import type { UserRole } from "../../auth/auth";
import ExpandButton from "./ExpandButton";

export type EntityCardAccent = "warning" | "error" | "grey";

interface Props {
  readonly backgroundColor?: string;
  readonly accent?: EntityCardAccent;
  readonly role: UserRole | undefined;
  readonly alignChildren?: "center";
  readonly childrenSpacing?: number;
  readonly onClick: () => void;
  readonly onContextMenu: () => void;
  readonly rightHandSideComponent?: ReactNode;
  readonly rightHandSideComponentSize?: number;
  readonly rightHandSideComponentExpandable?: boolean;
  readonly expandableComponent?: ReactNode;
  readonly onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  readonly onDragLeave?: (e: DragEvent<HTMLDivElement>) => void;
  readonly onDrop?: (e: DragEvent<HTMLDivElement>) => void;
}

const EntityCard: FC<PropsWithChildren<Props>> = ({
  children,
  backgroundColor,
  accent,
  role,
  alignChildren,
  childrenSpacing = 2,
  onClick,
  onContextMenu,
  rightHandSideComponent,
  rightHandSideComponentSize,
  rightHandSideComponentExpandable,
  expandableComponent,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  const [expanded, setExpanded] = useState(false);

  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const rightBlockSize = rightHandSideComponent !== undefined && isAdminOrAgent ? (rightHandSideComponentSize ?? 2) : 0;
  return (
    <Paper
      onContextMenu={onContextMenu}
      onClick={onClick}
      style={{ backgroundColor, display: "flow-root" }}
      sx={(theme) => accentStyle(theme, accent)}
      className="entity-card"
      data-testid="entity-card"
      data-accent={accent}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <Stack
        sx={{
          margin: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflow: "hidden",
          }}
        >
          <Grid
            container
            spacing={1}
            sx={{
              overflow: "hidden",
              flexGrow: 1,
            }}
          >
            <Grid
              container
              size={{ xs: 12, lg: 12 - rightBlockSize }}
              spacing={childrenSpacing}
              sx={{
                alignItems: alignChildren,
              }}
            >
              {children}
            </Grid>
            {rightHandSideComponent !== undefined && isAdminOrAgent && (
              <Grid
                size={{ xs: 12, lg: rightBlockSize }}
                sx={{
                  overflow: "hidden",
                }}
              >
                {!expanded || !rightHandSideComponentExpandable ? rightHandSideComponent : null}
              </Grid>
            )}
          </Grid>
          {isAdminOrAgent && expandableComponent && (
            <Stack>
              <ExpandButton expanded={expanded} onChange={setExpanded} />
            </Stack>
          )}
        </Stack>
        {isAdminOrAgent && expandableComponent && (
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
            mountOnEnter
            key={1}
            onClick={(e) => e.stopPropagation()}
          >
            {expandableComponent}
          </Collapse>
        )}
      </Stack>
    </Paper>
  );
};

function accentStyle(theme: Theme, accent: EntityCardAccent | undefined) {
  if (!accent) {
    return {};
  }
  const color = accent === "grey" ? theme.palette.grey[500] : theme.palette[accent].main;
  return { borderLeft: `6px solid ${color}`, bgcolor: theme.alpha(color, 0.08) };
}

export default EntityCard;
